import emptyFunction from 'react-utils/emptyFunction';
import partition from 'hs-lodash/partition';
import { Record as ImmutableRecord } from 'immutable';

// Only go up to arity 3, add more if needed
// Once we have a newer version of TS we can make this work with n-ary args:
//
// type Meep<In, Out> = [] | [(e: In) => Out] | [(e: In) => any, ...Array<(e: any) => any>, (e: any) => Out];
/*
 * This file contains generic object transformation methods that are applicable to most use cases.
 * It encapsulates the process of combining filters, comparators, mappers, so that transformation
 * code is easier to read and use. The more concrete usages of transformGeneric are in the files
 * named 'transformBlahBlahBlah`, such as 'transformProperty', 'transformContact'...
 */

export const emptyPartition = iterable => {
  return partition(iterable, emptyFunction.thatReturnsTrue);
};

/**
 * Takes in an array, then transforms it using the functions into a new array.
 * @param {Array} customFilters - filter functions
 * @param {Array} customSorts - sort functions
 * @param {Array} customMaps - map functions
 * @param {Array} array - the input array
 * @returns {Array} the transformed array
 */
export const genericFilterSortMap = (customFilters, customSorts, customMaps, array) => {
  let result = array;
  result = result.filter(item => {
    return customFilters.reduce(
    // Apply all filters, keep only values that pass ALL (&& chain) filters
    (shouldFilter, filter) => shouldFilter && filter(item), true);
  });
  customSorts.forEach(sorter => {
    result = result.sort(sorter);
  });
  customMaps.forEach(mapper => {
    result = result.map(mapper);
  });
  return result;
};

/**
 * Takes in an object or an array, then transforms it using the functions into a new array.
 * @param {Function} partitioner - partition function
 * @param {Array} filters - filter functions
 * @param {Array} sorters - sort functions
 * @param {Array} mappers - map functions
 * @param {Array|Object} objOrArray - the input data
 * @returns {Array} the transformed array
 */
export const genericFunnel = (partitioner, filters, sorters, mappers, objOrArray) => {
  // 1. Partition
  // (note: if objOrArray is an object:
  // the partitioner should convert it into an array of containing its object values)
  const parts = partitioner(objOrArray);
  // 2. Reduce using genericFilterSortMap
  return parts.flatMap(part => genericFilterSortMap(filters, sorters, mappers, part));
};
export const Matcher = ImmutableRecord({
  path: [],
  value: null
});